import { useState } from "react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { MARKET_TYPES, assetFromQuoteType } from "../lib/markets";
import { MagnifyingGlass, Plus, X, CircleNotch } from "@phosphor-icons/react";
import { fmt } from "../lib/signals";

export default function AddInstrument({ open, onClose, onAdded }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [mt, setMt] = useState(0);
  const [ticker, setTicker] = useState("");
  const [adding, setAdding] = useState("");

  if (!open) return null;

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/search`, { params: { q } });
      setResults(data.results || []);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const addResolved = async (payload, key) => {
    setAdding(key);
    try {
      const { data } = await api.post("/watchlist", payload);
      toast.success(`${payload.ticker} added & analysed`);
      onAdded?.(data.instrument);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setAdding(""); }
  };

  const addFromSearch = (r) =>
    addResolved({ ticker: r.symbol, exchange: "", asset_type: assetFromQuoteType(r.type), yahoo_symbol: r.symbol }, r.symbol);

  const addManual = () => {
    if (!ticker.trim()) return;
    const m = MARKET_TYPES[mt];
    addResolved({ ticker: ticker.trim(), exchange: m.exchange, asset_type: m.asset_type }, "manual");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-24 bg-foreground/20 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="add-instrument-modal" className="glass-strong rounded-3xl w-full max-w-lg p-6 fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Track an Instrument</h3>
          <button data-testid="add-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} weight="bold" /></button>
        </div>

        <form onSubmit={runSearch} className="flex gap-2 mb-3">
          <div className="flex-1 flex items-center gap-2 rounded-full bg-background/60 border border-border px-3">
            <MagnifyingGlass size={16} className="text-muted-foreground" />
            <input data-testid="add-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search any stock, index, commodity, forex, crypto…"
              className="flex-1 bg-transparent py-2.5 text-sm outline-none" />
          </div>
          <button data-testid="add-search-btn" type="submit" className="brand-gradient text-white rounded-full px-4 text-sm font-medium flex items-center gap-1">
            {searching ? <CircleNotch size={15} className="animate-spin" /> : "Search"}
          </button>
        </form>

        <div className="max-h-56 overflow-y-auto space-y-1 mb-4">
          {results.map((r) => (
            <div key={r.symbol} data-testid={`add-result-${r.symbol}`} className="flex items-center justify-between rounded-xl px-3 py-2 hover:bg-foreground/5">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{r.symbol} <span className="text-muted-foreground font-normal">· {r.exchange}</span></div>
                <div className="text-xs text-muted-foreground truncate">{r.name} · {r.type}</div>
              </div>
              <button data-testid={`add-btn-${r.symbol}`} onClick={() => addFromSearch(r)} disabled={adding === r.symbol}
                className="shrink-0 rounded-full bg-brand/10 text-brand hover:bg-brand/20 px-3 py-1.5 text-xs font-medium flex items-center gap-1">
                {adding === r.symbol ? <CircleNotch size={13} className="animate-spin" /> : <Plus size={13} weight="bold" />} Add
              </button>
            </div>
          ))}
          {results.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Search above, or add manually below.</div>}
        </div>

        <div className="border-t border-border pt-4">
          <div className="text-xs text-muted-foreground mb-2">Or add by symbol + market</div>
          <div className="flex gap-2">
            <select data-testid="add-market-type" value={mt} onChange={(e) => setMt(Number(e.target.value))}
              className="rounded-full bg-background/60 border border-border px-3 py-2 text-sm outline-none">
              {MARKET_TYPES.map((m, i) => <option key={m.label} value={i}>{m.label}</option>)}
            </select>
            <input data-testid="add-manual-ticker" value={ticker} onChange={(e) => setTicker(e.target.value)}
              placeholder="e.g. TCS, EURUSD, BTC" className="flex-1 rounded-full bg-background/60 border border-border px-4 py-2 text-sm outline-none" />
            <button data-testid="add-manual-btn" onClick={addManual} disabled={adding === "manual"}
              className="brand-gradient text-white rounded-full px-4 text-sm font-medium flex items-center gap-1">
              {adding === "manual" ? <CircleNotch size={15} className="animate-spin" /> : <Plus size={15} weight="bold" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
